import type { Camera, Light, Object3D, RenderItem, Scene } from "three";

/**
 * Confines a whole-scene render to the BRANCHES that hold a given set of
 * objects, for the length of one call — what `ambientOcclusion.ts` uses to
 * stop N8AO's translucent-object renders walking the models (2026-10-07).
 *
 * {@link isolate} hides every child of those objects' ancestors that is not
 * itself on a branch, and skips that child's matrix walk, so a render of the
 * scene never enters it; and it points `scene.traverse` at the branch nodes
 * only, so a sweep over "every object" sweeps those. Lights and cameras are
 * left alone: a render without the scene's lights would compile different
 * programs. {@link restore} puts all of it back and must run in a `finally`.
 *
 * Everything off the branches had its matrices updated by the same frame's
 * beauty render, and none of it can be drawn by the caller's render, so
 * hiding it and skipping its walk changes nothing that is drawn — PROVIDED
 * the objects passed in are every object that render could draw. The caller
 * owns that guarantee.
 */
export class TranslucentBranches {
  /** The scene, every ancestor of the objects, and each object's subtree. */
  private readonly m_branch = new Set<Object3D>();
  /** Ancestors only: the nodes whose OTHER children are hidden. */
  private readonly m_ancestors = new Set<Object3D>();
  private readonly m_hidden: Object3D[] = [];
  private readonly m_unwalked: Object3D[] = [];
  private m_ownTraverse: Scene["traverse"] | null = null;
  private m_active = false;

  private readonly m_skipWalk = (): void => {};
  private readonly m_traverseBranch = (callback: (node: Object3D) => unknown): void => {
    for (const node of this.m_branch) callback(node);
  };

  constructor(private readonly m_scene: Scene) {}

  /**
   * Isolates the branches holding `lists`' objects. With no objects at all,
   * every child of the scene but its lights and cameras is hidden: a render
   * then draws nothing, as it would have.
   */
  isolate(...lists: ReadonlyArray<ReadonlyArray<RenderItem>>): void {
    this.restore();
    const branch = this.m_branch;
    const ancestors = this.m_ancestors;
    branch.clear();
    ancestors.clear();
    for (const list of lists) {
      for (const item of list) {
        if (!branch.has(item.object)) item.object.traverse((node) => void branch.add(node));
      }
    }

    const scene = this.m_scene;
    branch.add(scene);
    ancestors.add(scene);
    for (const list of lists) {
      for (const item of list) {
        for (let node = item.object.parent; node !== null && !ancestors.has(node); node = node.parent) {
          ancestors.add(node);
          branch.add(node);
        }
      }
    }
    for (const ancestor of ancestors) {
      for (const child of ancestor.children) {
        if (
          branch.has(child)
          || (child as Light).isLight === true
          || (child as Camera).isCamera === true
        ) {
          continue;
        }
        if (child.visible) {
          child.visible = false;
          this.m_hidden.push(child);
        }
        if (!Object.prototype.hasOwnProperty.call(child, "updateMatrixWorld")) {
          child.updateMatrixWorld = this.m_skipWalk;
          this.m_unwalked.push(child);
        }
      }
    }
    this.m_ownTraverse = Object.prototype.hasOwnProperty.call(scene, "traverse") ? scene.traverse : null;
    scene.traverse = this.m_traverseBranch;
    this.m_active = true;
  }

  /** Undoes {@link isolate}. Idempotent. */
  restore(): void {
    if (!this.m_active) return;
    this.m_active = false;
    if (this.m_ownTraverse) this.m_scene.traverse = this.m_ownTraverse;
    else delete (this.m_scene as { traverse?: unknown }).traverse;
    this.m_ownTraverse = null;
    for (const node of this.m_hidden) node.visible = true;
    this.m_hidden.length = 0;
    for (const node of this.m_unwalked) delete (node as { updateMatrixWorld?: unknown }).updateMatrixWorld;
    this.m_unwalked.length = 0;
  }
}
